import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Calendar, 
  User, 
  ArrowLeft, 
  Tag,
  Share2,
  ChevronRight,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sample blog posts for fallback
const samplePosts = [
  {
    id: '1',
    title: 'Understanding Life Insurance: A Complete Guide for Canadian Families',
    slug: 'understanding-life-insurance-guide',
    excerpt: 'Life insurance is one of the most important financial decisions you can make. Learn about the different types of coverage and how to choose the right policy for your family.',
    content: `
      <h2>Why Life Insurance Matters</h2>
      <p>Life insurance is more than just a financial product—it's a promise to protect your loved ones when you're no longer there to provide for them. For Canadian families, understanding the nuances of life insurance can mean the difference between financial security and uncertainty.</p>
      
      <h2>Types of Life Insurance</h2>
      <h3>Term Life Insurance</h3>
      <p>Term life insurance provides coverage for a specific period, typically 10, 20, or 30 years. It's often the most affordable option and is ideal for:</p>
      <ul>
        <li>Young families with mortgage payments</li>
        <li>Parents with children who will eventually become financially independent</li>
        <li>Those looking for maximum coverage at the lowest cost</li>
      </ul>
      
      <h3>Whole Life Insurance</h3>
      <p>Whole life insurance provides permanent coverage that lasts your entire life. Key benefits include:</p>
      <ul>
        <li>Guaranteed death benefit</li>
        <li>Cash value accumulation</li>
        <li>Fixed premiums that never increase</li>
      </ul>
      
      <h3>Universal Life Insurance</h3>
      <p>Universal life combines permanent coverage with flexible premiums and investment options. This type offers:</p>
      <ul>
        <li>Flexibility in premium payments</li>
        <li>Investment component for wealth building</li>
        <li>Tax-advantaged growth</li>
      </ul>
      
      <h2>How Much Coverage Do You Need?</h2>
      <p>A common rule of thumb is to have coverage equal to 10-12 times your annual income. However, consider these factors:</p>
      <ul>
        <li>Outstanding debts (mortgage, car loans, credit cards)</li>
        <li>Future education costs for children</li>
        <li>Income replacement needs for your spouse</li>
        <li>Final expenses and estate costs</li>
      </ul>
      
      <h2>Next Steps</h2>
      <p>Ready to protect your family's future? Contact our team at Utmost Financial to discuss your options and get a personalized quote.</p>
    `,
    author: 'Justin Castillo',
    category: 'Insurance',
    image_url: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800',
    published: true,
    created_at: '2025-02-15T10:00:00Z'
  },
  {
    id: '2',
    title: 'First-Time Home Buyer Tips: Navigating the Canadian Market in 2025',
    slug: 'first-time-home-buyer-tips-2025',
    excerpt: 'Buying your first home is exciting but can be overwhelming. Here are essential tips to help you navigate the process and secure the best mortgage rates.',
    content: `
      <h2>Your Journey to Homeownership</h2>
      <p>Buying your first home is one of the most significant financial decisions you'll make. The Canadian housing market in 2025 presents unique opportunities and challenges for first-time buyers.</p>
      
      <h2>Government Programs for First-Time Buyers</h2>
      <h3>First Home Savings Account (FHSA)</h3>
      <p>The FHSA allows you to save up to $8,000 per year (lifetime limit of $40,000) with tax-deductible contributions and tax-free withdrawals for your first home purchase.</p>
      
      <h3>Home Buyers' Plan (HBP)</h3>
      <p>Withdraw up to $35,000 from your RRSP to purchase your first home. Repay over 15 years with no tax consequences if you follow the rules.</p>
      
      <h2>Getting Mortgage-Ready</h2>
      <ul>
        <li>Check and improve your credit score</li>
        <li>Save for a down payment (minimum 5% for homes under $500,000)</li>
        <li>Get pre-approved before house hunting</li>
        <li>Calculate all costs, including closing costs and property transfer tax</li>
      </ul>
      
      <h2>Working with a Mortgage Broker</h2>
      <p>A mortgage broker can access rates from multiple lenders, often finding better deals than you could on your own. At Utmost Financial, we work with over 100 lenders to find the best rate for your situation.</p>
    `,
    author: 'Linda Mallari',
    category: 'Mortgage',
    image_url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800',
    published: true,
    created_at: '2025-02-10T10:00:00Z'
  }
];

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
        fetchRelatedPosts(data.category);
      } else {
        // Fallback to sample posts
        const samplePost = samplePosts.find(p => p.slug === slug);
        if (samplePost) {
          setPost(samplePost);
          fetchRelatedPosts(samplePost.category);
        } else {
          navigate('/blog');
        }
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      const samplePost = samplePosts.find(p => p.slug === slug);
      if (samplePost) {
        setPost(samplePost);
        fetchRelatedPosts(samplePost.category);
      } else {
        navigate('/blog');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async (category) => {
    try {
      const response = await fetch(`${API_URL}/api/blog`);
      if (response.ok) {
        const data = await response.json();
        const related = data.filter(p => p.category === category && p.slug !== slug).slice(0, 3);
        setRelatedPosts(related.length > 0 ? related : samplePosts.filter(p => p.category === category && p.slug !== slug).slice(0, 3));
      } else {
        setRelatedPosts(samplePosts.filter(p => p.category === category && p.slug !== slug).slice(0, 3));
      }
    } catch (error) {
      setRelatedPosts(samplePosts.filter(p => p.category === category && p.slug !== slug).slice(0, 3));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Insurance': 'bg-blue-100 text-blue-700',
      'Mortgage': 'bg-green-100 text-green-700',
      'Investment': 'bg-purple-100 text-purple-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#0077B6] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-end overflow-hidden">
        {post.image_url && (
          <>
            <img 
              src={post.image_url} 
              alt={post.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </>
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-4 pb-12 w-full">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${getCategoryColor(post.category)}`}>
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-6 text-white/80 text-sm">
            <span className="flex items-center">
              <User className="h-4 w-4 mr-2" />
              {post.author}
            </span>
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              {formatDate(post.created_at)}
            </span>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="lg:flex lg:gap-12">
            {/* Main Content */}
            <article className="lg:flex-1">
              {/* Excerpt */}
              <p className="text-xl text-gray-600 leading-relaxed mb-8 font-medium border-l-4 border-[#D4AF37] pl-4">
                {post.excerpt}
              </p>

              {/* Article Content */}
              <div 
                className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:text-gray-600 prose-p:leading-relaxed prose-ul:text-gray-600 prose-li:mb-2 prose-a:text-[#0077B6] prose-a:no-underline hover:prose-a:underline"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '', { USE_PROFILES: { html: true } }) }}
              />

              {/* Share Section */}
              <div className="mt-12 pt-8 border-t">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-gray-500" />
                    <span className="font-semibold text-gray-700">Share this article:</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShare('facebook')}
                      className="rounded-full"
                    >
                      <Facebook className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShare('twitter')}
                      className="rounded-full"
                    >
                      <Twitter className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShare('linkedin')}
                      className="rounded-full"
                    >
                      <Linkedin className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShare('copy')}
                      className="rounded-full"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <Card className="mt-8 bg-gradient-to-r from-[#0077B6] to-[#005A8C] text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-2">Need Expert Advice?</h3>
                  <p className="text-blue-100 mb-6">
                    Our team of licensed professionals is here to help you make informed financial decisions.
                  </p>
                  <div className="flex gap-4 flex-wrap">
                    <Button asChild className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E]">
                      <Link to="/contact">Contact Us</Link>
                    </Button>
                    <Button asChild variant="outline" className="border-white text-white hover:bg-white/10">
                      <Link to="/team">Meet Our Team</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </article>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-[#F0F7FF]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Card key={relatedPost.id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                  {relatedPost.image_url && (
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={relatedPost.image_url} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${getCategoryColor(relatedPost.category)}`}>
                      {relatedPost.category}
                    </span>
                    <h3 className="font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-[#0077B6] transition-colors">
                      {relatedPost.title}
                    </h3>
                    <Link 
                      to={`/blog/${relatedPost.slug}`}
                      className="inline-flex items-center text-[#0077B6] text-sm font-medium"
                    >
                      Read More
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage;
